import { RecipeRatingUpdate } from '../types/recipe'
import { BaseApiService } from './BaseApiService'
import { clientLogger } from '../utils/clientLogger'

export interface FavoriteRecipe {
  id: string
  user_id: string
  recipe_data: any
  recipe_title: string
  recipe_hash: string
  thumbs_rating: -1 | 0 | 1 // -1 = thumbs down, 0 = no rating, 1 = thumbs up
  is_favorite: boolean
  user_rating: number | null // Reserved for future 1-5 star rating
  user_notes: string | null
  times_made: number
  created_at: string
  updated_at: string
}

// Backend API base URL
const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:3005'

class FavoritesApiService extends BaseApiService {
  constructor() {
    super({
      baseUrl: API_BASE_URL,
      defaultTimeout: 15000,
      defaultRetries: 3,
      enableRetry: true
    })
  }

  /**
   * Get all favorites for a user
   */
  async getUserFavorites(userId: string): Promise<FavoriteRecipe[]> {
    clientLogger.debug({ userId }, 'Fetching favorites for user')

    const result = await this.get<{ favorites: FavoriteRecipe[] }>(
      `/api/users/${userId}/favorites`
    )

    clientLogger.debug({ count: result.favorites?.length || 0 }, 'Found favorites')
    return result.favorites || []
  }

  /**
   * Get favorites filtered by is_favorite flag
   */
  async getFavoriteRecipes(userId: string): Promise<FavoriteRecipe[]> {
    clientLogger.debug({ userId }, 'Fetching favorited recipes for user')

    const result = await this.get<{ favorites: FavoriteRecipe[] }>(
      `/api/users/${userId}/favorites/favorited`
    )

    clientLogger.debug({ count: result.favorites?.length || 0 }, 'Found favorited recipes')
    return result.favorites || []
  }

  /**
   * Get a specific favorite by recipe hash
   */
  async getFavoriteByHash(userId: string, recipeHash: string): Promise<FavoriteRecipe | null> {
    const result = await this.get<{ favorite: FavoriteRecipe | null }>(
      `/api/users/${userId}/favorites/${recipeHash}`
    )
    return result.favorite
  }

  /**
   * Generate recipe hash from recipe name
   */
  generateRecipeHash(recipeName: string): string {
    // Simple hash based on recipe name
    return recipeName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
  }

  /**
   * Update or create a recipe rating/favorite
   */
  async updateRecipeRating(userId: string, update: RecipeRatingUpdate): Promise<FavoriteRecipe> {
    clientLogger.debug({ userId, recipeName: update.recipeName }, 'Updating recipe rating')

    const recipeHash = update.recipeHash || this.generateRecipeHash(update.recipeName)

    // Check if this recipe already exists in favorites
    const existing = await this.getFavoriteByHash(userId, recipeHash)

    // Validation: Cannot favorite a thumbs-downed recipe
    if (update.isFavorite === true && (update.thumbsRating === -1 || existing?.thumbs_rating === -1)) {
      clientLogger.warn({}, 'Cannot favorite a thumbs-downed recipe')
      throw new Error('Cannot favorite a recipe that has been thumbs-downed')
    }

    // Prepare payload for backend API
    const payload = {
      recipeHash,
      recipeName: update.recipeName,
      sessionId: update.sessionId,
      thumbsRating: update.thumbsRating !== undefined ? update.thumbsRating : (existing?.thumbs_rating ?? 0),
      isFavorite: update.isFavorite !== undefined ? update.isFavorite : (existing?.is_favorite ?? false),
      userNotes: update.userNotes
    }

    // If setting to thumbs down, remove favorite
    if (payload.thumbsRating === -1) {
      payload.isFavorite = false
    }

    const result = await this.post<{ favorite: FavoriteRecipe }>(
      `/api/users/${userId}/favorites`,
      payload
    )

    clientLogger.debug({ favorite: result.favorite }, 'Updated favorite')
    return result.favorite
  }

  /**
   * Toggle thumbs up rating
   */
  async toggleThumbsUp(userId: string, recipeName: string, sessionId?: string): Promise<FavoriteRecipe> {
    const recipeHash = this.generateRecipeHash(recipeName)
    const existing = await this.getFavoriteByHash(userId, recipeHash)

    const newRating = existing?.thumbs_rating === 1 ? 0 : 1

    return this.updateRecipeRating(userId, {
      recipeHash,
      recipeName,
      sessionId,
      thumbsRating: newRating as -1 | 0 | 1
    })
  }

  /**
   * Toggle thumbs down rating
   */
  async toggleThumbsDown(userId: string, recipeName: string, sessionId?: string): Promise<FavoriteRecipe> {
    const recipeHash = this.generateRecipeHash(recipeName)
    const existing = await this.getFavoriteByHash(userId, recipeHash)

    const newRating = existing?.thumbs_rating === -1 ? 0 : -1

    return this.updateRecipeRating(userId, {
      recipeHash,
      recipeName,
      sessionId,
      thumbsRating: newRating as -1 | 0 | 1,
      // If thumbs down, also remove from favorites
      isFavorite: newRating === -1 ? false : existing?.is_favorite
    })
  }

  /**
   * Toggle favorite status
   */
  async toggleFavorite(userId: string, recipeName: string, sessionId?: string): Promise<FavoriteRecipe> {
    const recipeHash = this.generateRecipeHash(recipeName)
    const existing = await this.getFavoriteByHash(userId, recipeHash)

    // Cannot favorite if thumbs down
    if (existing?.thumbs_rating === -1) {
      throw new Error('Cannot favorite a recipe that has been thumbs-downed')
    }

    const newFavoriteStatus = !(existing?.is_favorite ?? false)

    return this.updateRecipeRating(userId, {
      recipeHash,
      recipeName,
      sessionId,
      isFavorite: newFavoriteStatus
    })
  }

  /**
   * Delete a favorite/rating
   */
  async deleteRecipeRating(userId: string, recipeHash: string): Promise<void> {
    clientLogger.debug({ recipeHash }, 'Deleting recipe rating')

    await this.delete<{ success: boolean }>(
      `/api/users/${userId}/favorites/${recipeHash}`
    )

    clientLogger.debug({}, 'Deleted favorite')
  }

  /**
   * Increment times_made counter
   */
  async incrementTimesMade(userId: string, recipeHash: string): Promise<void> {
    await this.put<{ success: boolean, favorite: FavoriteRecipe }>(
      `/api/users/${userId}/favorites/${recipeHash}/times-made`
    )

    clientLogger.debug({ recipeHash }, 'Incremented times_made')
  }

  /**
   * DEPRECATED: Guest localStorage methods (replaced by backend API + staging tables)
   * Kept for backward compatibility during migration
   */
  getGuestFavorites(guestSessionId: string): FavoriteRecipe[] {
    clientLogger.warn({}, 'getGuestFavorites() is deprecated - use getUserFavorites() instead')
    const key = `guest_favorites_${guestSessionId}`
    const stored = localStorage.getItem(key)
    return stored ? JSON.parse(stored) : []
  }

  saveGuestFavorites(guestSessionId: string, favorites: FavoriteRecipe[]): void {
    clientLogger.warn({}, 'saveGuestFavorites() is deprecated - use updateRecipeRating() instead')
    const key = `guest_favorites_${guestSessionId}`
    localStorage.setItem(key, JSON.stringify(favorites))
  }

  /**
   * DEPRECATED: Migrate guest favorites (now handled by backend UserService.promoteOrLinkUser)
   */
  async migrateGuestFavorites(guestSessionId: string, _userId: string): Promise<void> {
    clientLogger.warn({}, 'migrateGuestFavorites() is deprecated - migration handled by backend promotion flow')

    // Clear localStorage as cleanup (no longer needed with staging tables)
    localStorage.removeItem(`guest_favorites_${guestSessionId}`)
    clientLogger.debug({}, 'Cleared localStorage guest favorites')
  }
}

export const favoritesApiService = new FavoritesApiService()
